import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "motion/react";
import { X, Copy, Check, FileCode, ArrowRight } from "lucide-react";
import type { PromptDefinition } from "@/data/prompts";
import { PROMPT_CATEGORY_LABELS, WORKFLOWS } from "@/data/prompts";

interface PromptDetailModalProps {
  prompt: PromptDefinition;
  onClose: () => void;
}

export default function PromptDetailModal({ prompt, onClose }: PromptDetailModalProps) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(prompt.promptText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const categoryLabel = PROMPT_CATEGORY_LABELS[prompt.category];
  const workflowNames = prompt.usedIn.map(id => WORKFLOWS[id]?.name || id);

  const modalContent = (
    <AnimatePresence>
      <div
        className="fixed inset-0 flex items-center justify-center p-4"
        style={{ zIndex: 9999 }}
      >
        {/* Backdrop */}
        <motion.div
          className="absolute inset-0 bg-black/50 backdrop-blur-md"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        />

        {/* Modal */}
        <motion.div
          className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl border border-white/20 shadow-2xl"
          style={{
            background: "linear-gradient(to bottom right, #063F6B, #042a4d)",
            zIndex: 10000,
          }}
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ duration: 0.3 }}
        >
          {/* Header */}
          <div className="sticky top-0 p-6 border-b border-white/10 bg-[#063F6B]">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-500/20 text-amber-400">
                    {categoryLabel}
                  </span>
                </div>
                <h2 className="text-xl font-bold text-white">{prompt.name}</h2>
              </div>
              <button
                onClick={onClose}
                className="p-2.5 rounded-lg hover:bg-white/10 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center ml-4"
              >
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Description */}
            <section>
              <p className="text-slate-300">{prompt.description}</p>
            </section>

            {/* Used In */}
            <section>
              <h3 className="text-sm font-medium text-slate-400 mb-2 flex items-center gap-2">
                <ArrowRight className="w-4 h-4" />
                Used In
              </h3>
              <div className="flex flex-wrap gap-2">
                {workflowNames.map(name => (
                  <span
                    key={name}
                    className="px-3 py-1.5 rounded-lg text-sm bg-white/10 text-slate-300 border border-white/10"
                  >
                    {name}
                  </span>
                ))}
              </div>
            </section>

            {/* Source File */}
            <section>
              <h3 className="text-sm font-medium text-slate-400 mb-2 flex items-center gap-2">
                <FileCode className="w-4 h-4" />
                Source File
              </h3>
              <code className="text-sm text-slate-400 bg-black/30 px-3 py-1.5 rounded-lg block overflow-x-auto">
                {prompt.sourceFile}
              </code>
            </section>

            {/* Prompt Text */}
            <section>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-white">Full Prompt</h3>
                <button
                  onClick={handleCopy}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm bg-white/10 hover:bg-white/15 text-slate-300 transition-colors border border-white/10"
                >
                  {copied ? (
                    <>
                      <Check className="w-3.5 h-3.5 text-green-400" />
                      <span className="text-green-400">Copied!</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-3.5 h-3.5" />
                      Copy
                    </>
                  )}
                </button>
              </div>
              <div className="p-4 bg-black/30 border border-white/10 rounded-xl">
                <pre className="text-xs text-slate-400 whitespace-pre-wrap font-mono leading-relaxed">
                  {prompt.promptText}
                </pre>
              </div>
            </section>
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 p-6 border-t border-white/10 bg-[#042a4d]">
            <button
              onClick={onClose}
              className="w-full px-6 py-3 rounded-xl bg-white/10 hover:bg-white/15 text-white font-medium transition-colors border border-white/10"
            >
              Close
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );

  return createPortal(modalContent, document.body);
}
